/**
 * The world acting on itself — docs/01's stated differentiator, and docs/07
 * Stage 5.2.
 *
 * ## Data, not a switch statement
 *
 * Every event is an entry in `WORLD_EVENTS` describing how big it is, how long it
 * lasts, what it scales and whether it rewrites the ground. This module knows how
 * to *run* an event; it knows nothing about what any particular one does. The
 * roadmap asks for that explicitly, and the reason is practical: events get added,
 * retuned and removed constantly during balance, and a switch statement turns each
 * of those into a code change with its own chance of a bug.
 *
 * Adding a fifth event is a `WORLD_EVENTS` entry and nothing here.
 *
 * ## Determinism
 *
 * Scheduling draws from `state.eventRngState`, a plain word stepped with
 * `rngStep`, seeded from the session seed. Same seed, same events, at the same
 * ticks, forever — which is what makes "seed 1042 gets wrecked by a drought at
 * minute nine" reproducible rather than a story. No `Math.random()`, and no
 * closure, so `GameState` stays serializable for the Phase 10 resync docs/04
 * wants.
 *
 * ## Tick-time only
 *
 * Everything here mutates game state and is therefore called only from inside a
 * tick, like every other mutation in the server.
 */
import { MAX_CONCURRENT_WORLD_EVENTS, PROVOKE_RADIUS, WORLD_EVENTS, WORLD_EVENT_GRACE_SECONDS, WORLD_EVENT_MAX_INTERVAL_SECONDS, WORLD_EVENT_MIN_INTERVAL_SECONDS, hexKey, hexSpiral, } from "kngdm-shared";
import { TICK_RATE_HZ } from "../tick.js";
import { rngStep } from "./rng.js";
import { markDirty } from "./state.js";
/** Draw the next uniform value, advancing the state word in place. */
function nextRandom(state) {
    const { value, next } = rngStep(state.eventRngState);
    state.eventRngState = next;
    return value;
}
/** Uniform integer in [min, max). */
function nextInt(state, min, max) {
    if (!(max > min))
        return min;
    return min + Math.floor(nextRandom(state) * (max - min));
}
/** The first tick an event may land on, and the schedule seed. */
export function initialEventTick() {
    return Math.round(WORLD_EVENT_GRACE_SECONDS * TICK_RATE_HZ);
}
/**
 * Advance the world-event system by one tick.
 *
 * Retire what has expired, then land whatever is due. Retiring first matters:
 * an event ending on the same tick another begins should free its slot, or the
 * concurrency cap silently costs a session one event every time that coincides.
 */
export function stepWorldEvents(state) {
    retireExpired(state);
    if (state.tick < state.nextEventTick)
        return [];
    // Reschedule regardless of whether one actually lands, so a session at the
    // concurrency cap doesn't fire a burst the instant a slot frees up.
    scheduleNext(state);
    if (state.activeEvents.length >= MAX_CONCURRENT_WORLD_EVENTS)
        return [];
    const type = chooseEventType(state);
    if (type === null)
        return [];
    const centre = chooseCentre(state);
    if (centre === null)
        return [];
    const landed = landEvent(state, type, centre, WORLD_EVENTS[type].radius);
    return landed === null ? [] : [landed];
}
function retireExpired(state) {
    if (state.activeEvents.length === 0)
        return;
    const survivors = [];
    for (const active of state.activeEvents) {
        if (state.tick < active.endsAtTick) {
            survivors.push(active);
            continue;
        }
        // The hexes stop being scaled the moment the record goes, but they are still
        // broadcast this tick, so mark them so the client clears its overlay.
        for (const key of active.hexKeys) {
            const record = state.hexes.get(key);
            if (record !== undefined)
                markDirty(state, record);
        }
    }
    state.activeEvents = survivors;
}
function scheduleNext(state) {
    const span = Math.max(1, Math.round((WORLD_EVENT_MAX_INTERVAL_SECONDS - WORLD_EVENT_MIN_INTERVAL_SECONDS) * TICK_RATE_HZ));
    const min = Math.round(WORLD_EVENT_MIN_INTERVAL_SECONDS * TICK_RATE_HZ);
    state.nextEventTick = state.tick + min + nextInt(state, 0, span);
}
/**
 * Weighted draw over the enabled events.
 *
 * A weight of 0 disables an event without removing it from the table, which is
 * how `migration` sits visibly unimplemented rather than silently missing.
 */
function chooseEventType(state) {
    const entries = Object.values(WORLD_EVENTS).filter((definition) => definition.weight > 0);
    let total = 0;
    for (const definition of entries)
        total += definition.weight;
    if (total <= 0)
        return null;
    let roll = nextRandom(state) * total;
    for (const definition of entries) {
        roll -= definition.weight;
        if (roll <= 0)
            return definition.type;
    }
    /* c8 ignore next */
    return entries[entries.length - 1]?.type ?? null;
}
/**
 * Where it lands: a uniformly chosen hex on the map.
 *
 * Deliberately not biased toward players. docs/01 wants a world that runs on its
 * own logic rather than a difficulty dial — an event that hunts the leader is a
 * rubber band wearing a volcano costume, and players notice.
 */
function chooseCentre(state) {
    const keys = [...state.hexes.keys()];
    if (keys.length === 0)
        return null;
    const key = keys[nextInt(state, 0, keys.length)];
    if (key === undefined)
        return null;
    return state.hexes.get(key)?.coord ?? null;
}
/**
 * Apply an event at a location, returning what it touched.
 *
 * Terrain rewrites happen once, here — not per tick — because they are permanent
 * by design: the ash stays fertile after the eruption stops. The yield scaling is
 * the opposite; it lives in the active record and evaporates when the event ends.
 */
export function landEvent(state, type, centre, radius) {
    const definition = WORLD_EVENTS[type];
    const hexKeys = new Set();
    const affectedHexes = [];
    for (const coord of hexSpiral(centre, Math.max(0, radius))) {
        const key = hexKey(coord);
        const record = state.hexes.get(key);
        if (record === undefined)
            continue;
        hexKeys.add(key);
        affectedHexes.push(record.coord);
        if (definition.transformsBiome !== null)
            record.biome = definition.transformsBiome;
        // Never overwrites an existing special: a meteor landing on a geyser should
        // not quietly delete the rarest thing on the map.
        if (definition.leavesSpecial !== null && record.special === null) {
            record.special = definition.leavesSpecial;
        }
        markDirty(state, record);
    }
    if (hexKeys.size === 0)
        return null;
    const durationTicks = Math.round(definition.durationSeconds * TICK_RATE_HZ);
    state.eventCounter += 1;
    // A zero-duration event still gets a record for exactly one tick, so it is
    // broadcast and rendered before it retires. Without that, a meteor would be
    // invisible — the crater would simply appear.
    state.activeEvents.push({
        id: `e${state.eventCounter}`,
        event: type,
        startedAtTick: state.tick,
        endsAtTick: state.tick + Math.max(1, durationTicks),
        hexKeys,
    });
    return { event: type, affectedHexes };
}
/**
 * End every event acting on a hex — the `calm` god tool.
 *
 * Ends the whole event, not just its grip on the one hex, and that is the
 * intended generosity: Aether is scarce, and a tool that cost a third of a
 * kingdom's reserve to shrink a drought by one hex would never be worth using.
 */
export function calmEventsAt(state, coord) {
    const key = hexKey(coord);
    const survivors = [];
    let ended = 0;
    for (const active of state.activeEvents) {
        if (!active.hexKeys.has(key)) {
            survivors.push(active);
            continue;
        }
        ended += 1;
        for (const affected of active.hexKeys) {
            const record = state.hexes.get(affected);
            if (record !== undefined)
                markDirty(state, record);
        }
    }
    state.activeEvents = survivors;
    return ended;
}
/**
 * Call a disaster down on a hex — the `provoke` god tool.
 *
 * The type is drawn from the same weighted table the world uses, so a player
 * cannot pick a favourable disaster; they choose *where*, and the world chooses
 * *what*. That keeps it a gamble rather than a precision weapon, and it means
 * provoking is priced against an average outcome rather than the best one.
 */
export function provokeEventAt(state, coord) {
    const type = chooseEventType(state);
    if (type === null)
        return null;
    return landEvent(state, type, coord, PROVOKE_RADIUS);
}
/** True if any active event is currently acting on this hex. */
export function isUnderEvent(state, coord) {
    const key = hexKey(coord);
    return state.activeEvents.some((active) => active.hexKeys.has(key));
}
//# sourceMappingURL=worldEvents.js.map